import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { Button } from '../components/ui';
import { getAnalysisHistory } from '../services/api';
import type { AnalysisHistoryItem } from '../types';

type ResultsTab = 'photo' | 'ratings' | 'analysis' | 'tips';

function ResultsDisplay({ result }: { result: AnalysisHistoryItem }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ResultsTab>('photo');
  const [activeTip, setActiveTip] = useState<AnalysisHistoryItem['tips'][number] | null>(null);
  const primaryImage = result.imgUrls?.[0];
  const isPSLRating = Boolean(result.interpretation);

  const ratingColor = (value: number) => {
    if (isPSLRating) {
      if (value >= 7) return 'bg-green-500';
      if (value >= 5) return 'bg-lime-400';
      if (value >= 3) return 'bg-yellow-400';
      return 'bg-orange-400';
    }
    if (value >= 91) return 'bg-blue-500';
    if (value >= 75) return 'bg-green-500';
    if (value >= 50) return 'bg-yellow-400';
    if (value >= 25) return 'bg-orange-400';
    return 'bg-red-500';
  };

  const ratingTextColor = (value: number) => {
    if (isPSLRating) {
      if (value >= 7) return 'text-green-400';
      if (value >= 5) return 'text-lime-400';
      if (value >= 3) return 'text-yellow-300';
      return 'text-orange-300';
    }
    if (value >= 91) return 'text-blue-400';
    if (value >= 75) return 'text-green-400';
    if (value >= 50) return 'text-yellow-300';
    if (value >= 25) return 'text-orange-400';
    return 'text-red-400';
  };

  const handleShare = async () => {
    const storedFront = localStorage.getItem('umax-front-base64');
    const shareImageSrc = storedFront || primaryImage;
    if (!shareImageSrc) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 1080;
    canvas.height = 1920;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = shareImageSrc;

    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.src = `${import.meta.env.BASE_URL || '/'}looksmax-icon.png`;

    await Promise.all([
      new Promise<void>((resolve) => {
        image.onload = () => resolve();
        image.onerror = () => resolve();
      }),
      new Promise<void>((resolve) => {
        logo.onload = () => resolve();
        logo.onerror = () => resolve();
      }),
    ]);

    const circleSize = 360;
    const circleX = canvas.width / 2;
    const circleY = 280;

    ctx.save();
    ctx.beginPath();
    ctx.arc(circleX, circleY, circleSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    if (image.complete && image.naturalWidth) {
      const aspect = image.naturalWidth / image.naturalHeight;
      const drawW = circleSize * (aspect >= 1 ? aspect : 1);
      const drawH = circleSize * (aspect >= 1 ? 1 : 1 / aspect);
      ctx.drawImage(
        image,
        circleX - drawW / 2,
        circleY - drawH / 2,
        drawW,
        drawH
      );
    }
    ctx.restore();

    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(circleX, circleY, circleSize / 2, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = '700 48px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(isPSLRating ? 'Your PSL Snapshot' : 'Your AI Face Ratings', canvas.width / 2, 520);

    let ratingStartY = 610;
    const cardW = 420;
    const cardH = 130;
    const gapX = 40;
    const gapY = 32;
    const shareBarColor = (value: number) => {
      if (isPSLRating) {
        if (value >= 7) return '#22c55e';
        if (value >= 5) return '#a3e635';
        if (value >= 3) return '#facc15';
        return '#fb923c';
      }
      if (value >= 91) return '#3b82f6';
      if (value >= 75) return '#22c55e';
      if (value >= 50) return '#facc15';
      if (value >= 25) return '#fb923c';
      return '#f87171';
    };

    if (isPSLRating && result.interpretation) {
      const blockX = 150;
      const blockY = 585;
      const blockW = canvas.width - blockX * 2;
      const blockH = 230;

      ctx.fillStyle = '#141414';
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(blockX, blockY, blockW, blockH, 24);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 34px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(result.interpretation.facialArchetype, canvas.width / 2, blockY + 48);

      ctx.font = '500 20px Inter, sans-serif';
      ctx.textAlign = 'left';
      const leftX = blockX + 24;
      const rightX = blockX + blockW - 24;
      let rowY = blockY + 86;

      const row = (label: string, value: string) => {
        ctx.fillStyle = '#a0a0a0';
        ctx.fillText(label, leftX, rowY);
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'right';
        ctx.fillText(value, rightX, rowY);
        ctx.textAlign = 'left';
        rowY += 32;
      };

      row('Genetic Ceiling', result.interpretation.geneticCeiling);
      row('Primary Strength', result.interpretation.primaryStrength);
      row('Primary Bottleneck', result.interpretation.primaryBottleneck);
      row('Looksmax Priority', result.interpretation.looksmaxPriority);

      ratingStartY = blockY + blockH + 24;
    }

    result.ratings.slice(0, 6).forEach((rating, idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const x = (canvas.width - (cardW * 2 + gapX)) / 2 + col * (cardW + gapX);
      const y = ratingStartY + row * (cardH + gapY);

      ctx.fillStyle = '#141414';
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(x, y, cardW, cardH, 24);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#a0a0a0';
      ctx.font = '500 22px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(rating.label, x + 24, y + 40);

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 40px Inter, sans-serif';
      ctx.fillText(
        isPSLRating ? Number(rating.value).toFixed(1) : String(rating.value),
        x + 24,
        y + 88
      );

      ctx.fillStyle = '#1f1f1f';
      ctx.fillRect(x + 24, y + 100, cardW - 48, 10);
      ctx.fillStyle = shareBarColor(rating.value);
      const normalizedValue = isPSLRating
        ? Math.min(100, (rating.value / 8) * 100)
        : Math.min(100, Math.max(0, rating.value));
      ctx.fillRect(x + 24, y + 100, (cardW - 48) * (normalizedValue / 100), 10);
    });

    const footerY = canvas.height - 120;
    if (logo.complete && logo.naturalWidth) {
      const logoSize = 64;
      ctx.drawImage(logo, 90, footerY - 32, logoSize, logoSize);
    }
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 28px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Looksmaxxing', 170, footerY + 8);
    ctx.fillStyle = '#a0a0a0';
    ctx.font = '500 20px Inter, sans-serif';
    ctx.fillText('AI face rating app', 170, footerY + 38);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png')
    );
    if (!blob) return;

    const file = new File([blob], 'looksmaxxing-results.png', { type: 'image/png' });

    // Only use Web Share API on mobile (desktop support is unreliable)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: isPSLRating ? 'PSL Maxxing - Results' : 'Looksmaxxing - AI Face Rating',
          text: isPSLRating ? 'My PSL results' : 'My AI face ratings',
          files: [file],
        });
        return;
      } catch {
        // Fall through to download if share fails
      }
    }

    // Download fallback for desktop and failed shares
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'looksmaxxing-results.png';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="px-6 py-6 max-w-md mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button
            className="text-secondary text-sm"
            onClick={() => navigate('/')}
          >
            Close
          </button>
          <h1 className="text-base font-semibold text-white">
            {tab === 'photo' && 'Photo'}
            {tab === 'ratings' && (isPSLRating ? 'PSL Scores' : 'Ratings')}
            {tab === 'analysis' && 'Analysis'}
            {tab === 'tips' && 'Tips'}
          </h1>
          <button className="text-secondary text-sm" onClick={handleShare}>
            Save
          </button>
        </div>

        <div className="flex justify-between gap-2 mb-6">
          {([
            { id: 'photo', label: 'Photo' },
            { id: 'ratings', label: 'Ratings' },
            { id: 'analysis', label: 'Analysis' },
            { id: 'tips', label: 'Tips' },
          ] as const).map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`flex-1 rounded-full border px-3 py-2 text-xs font-semibold ${
                tab === item.id
                  ? 'border-accent text-white bg-accent/10'
                  : 'border-white/10 text-secondary'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === 'photo' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-40 h-40 rounded-2xl overflow-hidden border-2 border-white/15 bg-surface">
                {primaryImage ? (
                  <img src={primaryImage} alt="Result" className="w-full h-full object-cover" />
                ) : null}
              </div>
            </div>
            <div className="text-center">
              <h2 className="text-xl font-semibold text-white">
                {isPSLRating ? 'Your PSL Snapshot' : 'Your Rating Snapshot'}
              </h2>
              <p className="text-secondary text-sm mt-2">
                This image was used for scoring
              </p>
            </div>
          </>
        )}

        {tab === 'ratings' && (
          <>
            {isPSLRating && result.interpretation && (
              <div className="mb-5">
                <h2 className="text-2xl font-bold text-white text-center mb-3">
                  {result.interpretation.facialArchetype}
                </h2>
                <div className="rounded-2xl bg-surface border border-white/10 p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary">Genetic Ceiling</span>
                    <span className="text-white font-semibold">{result.interpretation.geneticCeiling}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary">Primary Strength</span>
                    <span className="text-white font-semibold">{result.interpretation.primaryStrength}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary">Primary Bottleneck</span>
                    <span className="text-white font-semibold">{result.interpretation.primaryBottleneck}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary">Looksmax Priority</span>
                    <span className="text-white font-semibold">{result.interpretation.looksmaxPriority}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {result.ratings.map((rating) => (
                <div
                  key={rating.id}
                  className="rounded-2xl bg-surface border border-white/10 p-4"
                >
                  <p className="text-xs text-secondary">{rating.label}</p>
                  <p className={`text-2xl font-bold mt-1 ${ratingTextColor(rating.value)}`}>
                    {isPSLRating ? rating.value.toFixed(1) : rating.value}
                  </p>
                  <div className="mt-3 h-2 w-full rounded-full bg-white/10 overflow-hidden">
                    {(() => {
                      const normalized = isPSLRating
                        ? Math.min(100, (rating.value / 8) * 100)
                        : Math.min(100, Math.max(0, rating.value));
                      return (
                    <div
                      className={`h-full ${ratingColor(rating.value)}`}
                      style={{ width: `${normalized}%` }}
                    />
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-secondary mt-4 text-center">
              Tip: Save or share these results—one-time unlock means you won’t see them again.
            </p>
          </>
        )}

        {tab === 'analysis' && (
          <div className="space-y-3">
            {result.physiognomy.map((item) => (
              <div
                key={item.id}
                className="rounded-2xl bg-surface border border-white/10 px-4 py-3 flex items-center justify-between"
              >
                <span className="text-secondary text-sm">{item.label}</span>
                <span className="text-white font-semibold">{item.value}</span>
              </div>
            ))}
          </div>
        )}

        {tab === 'tips' && (
          <div className="space-y-3">
            {result.tips.map((tip) => (
              <button
                key={tip.id}
                className="rounded-2xl bg-surface border border-white/10 px-4 py-4"
                onClick={() => setActiveTip(tip)}
              >
                <p className="text-white font-semibold">{tip.title}</p>
                <p className="text-secondary text-sm mt-1">{tip.shortDescription}</p>
              </button>
            ))}
          </div>
        )}

        <div className="mt-8">
          <Button onClick={handleShare}>
            Share
          </Button>
        </div>
      </div>

      {activeTip && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center px-6 z-50">
          <div className="w-full max-w-md rounded-2xl bg-surface border border-white/10 p-5">
            <div className="flex items-start justify-between mb-3">
              <h2 className="text-lg font-semibold text-white">{activeTip.title}</h2>
              <button
                className="text-secondary"
                onClick={() => setActiveTip(null)}
              >
                Close
              </button>
            </div>
            <p className="text-secondary text-sm leading-relaxed">{activeTip.body}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function ResultsScreen() {
  const location = useLocation();
  const { analysis } = useStore();
  const [result, setResult] = useState<AnalysisHistoryItem | null>(
    (location.state as { result?: AnalysisHistoryItem } | null)?.result || (analysis as AnalysisHistoryItem | null)
  );

  useEffect(() => {
    if (result) return;
    const loadLatest = async () => {
      try {
        const history = await getAnalysisHistory(useStore.getState().user.userId);
        if (history.items && history.items.length > 0) {
          setResult(history.items[0]);
        }
      } catch {
        // ignore
      }
    };
    loadLatest();
  }, [result]);

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <p className="text-secondary">Loading results...</p>
      </div>
    );
  }

  return <ResultsDisplay result={result} />;
}
